import { computed, CSSProperties, ComputedRef, Ref } from 'vue';
import { getCurrentTheme } from '@/common/utils/commonUtils';
import { IconType } from '@/types/icon.types';
import { useColumnPinning } from '../column/useColumnPinning';
import type {
  GridRawRowDataType,
  GridTableInstance,
  GridHeaderInstance,
  GridColumnInstance,
  GridStyleOption,
  GridSortingOption,
  GridColumnVisibilityOption,
  GridColumnResizingOption,
  GridColumnPinningOption,
  GridColumnOrderingOption,
  GridExpandingOption,
} from '../../modernGrid.types';

export const useHeaderTemplate = <RowDataType extends GridRawRowDataType>({
  table,
  styleInfo,
  columnResizingInfo,
  columnPinningInfo,
  columnOrderingInfo,
  hasGroupedColumns,
  expandingInfo,
  expandingColumn,
}: {
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  styleInfo: ComputedRef<GridStyleOption>;
  sortingInfo: ComputedRef<GridSortingOption>;
  columnVisibilityInfo: ComputedRef<GridColumnVisibilityOption>;
  columnResizingInfo: ComputedRef<GridColumnResizingOption>;
  columnPinningInfo: ComputedRef<GridColumnPinningOption>;
  columnOrderingInfo: ComputedRef<GridColumnOrderingOption>;
  hasGroupedColumns: Ref<boolean>;
  expandingInfo: ComputedRef<GridExpandingOption>;
  expandingColumn: Ref<string>;
}) => {
  const { getPinnedCellStyle, getPinnedClasses } = useColumnPinning<RowDataType>({
    table,
    columnPinningInfo,
  });

  const getHeaderCellAttributes = (header: GridHeaderInstance<RowDataType>) => {
    const { column } = header;
    const isGroup = column.columns?.length > 0;
    const groupId = column.parent?.id || 'root';
    const isPinned = column.getIsPinned();

    return {
      'data-column-id': column.id,
      'data-group-id': groupId,
      'data-depth': header.depth,
      'data-is-group': isGroup,
      'data-is-pinned': isPinned,
    };
  };

  const getHeaderCellStyle = (
    header: GridHeaderInstance<RowDataType>,
    headers: GridHeaderInstance<RowDataType>[],
  ): CSSProperties => {
    const baseStyle = {
      width: `${header.getSize()}px`,
      height: `${styleInfo.value.header?.height || styleInfo.value.body?.rowHeight}px`,
    };
    const pinnedStyle = getPinnedCellStyle(header, headers);
    return {
      ...baseStyle,
      ...(pinnedStyle || {}),
    };
  };

  const getHeaderCellClasses = (header: GridHeaderInstance<RowDataType>) => {
    const column = header.column as GridColumnInstance<RowDataType>;
    const { use: useOrdering, useHandleBtn } = columnOrderingInfo.value;
    return [
      'modern-grid-header__cell',
      `${header.id}`,
      {
        'is-toggled': column.columnDef.meta?.attrs?.toggle,
        sortable: column.getCanSort(),
        resizable: columnResizingInfo.value.use && column.getCanResize(),
        'is-resizing': column.getIsResizing(),
        'not-orderable': useOrdering && !useHandleBtn && !getUseHeaderCellOrdering(column),
        ...getPinnedClasses(header),
      },
    ].filter(Boolean);
  };

  const getUseHeaderCellOrdering = (column: GridColumnInstance<RowDataType>): boolean => {
    const { use: useOrdering, useHandleBtn } = columnOrderingInfo.value;
    return (
      !column.getIsPinned() &&
      expandingColumn.value !== column.id &&
      useOrdering &&
      !useHandleBtn &&
      !hasGroupedColumns.value
    );
  };

  const getCellHeight = (header: GridHeaderInstance<RowDataType>): number => {
    const { depth: headerDepth, headerGroup, subHeaders } = header;
    const { depth: columnDepth } = header.column;
    const rowHeight = styleInfo.value.header?.height || styleInfo.value.body?.rowHeight || 0;
    const rowSpan = headerDepth - columnDepth;
    const borderWidth = 1;
    if (rowSpan === 1 && columnDepth === headerGroup.depth) {
      return rowHeight;
    }
    if (columnDepth < headerGroup.depth && !subHeaders.length) {
      return (
        rowHeight * rowSpan +
        (styleInfo.value.header?.showRowLine ? borderWidth * (rowSpan - 1) : 0)
      );
    }
    return 0;
  };

  const getIsVisibleOrderingHandle = (column: GridColumnInstance<RowDataType>): boolean => {
    const { use: useOrdering, useHandleBtn } = columnOrderingInfo.value;
    if (!useOrdering || !useHandleBtn || column.getIsPinned() || hasGroupedColumns.value)
      return false;
    return !(expandingColumn.value === column.id);
  };

  const getClickableHeader = (header: GridHeaderInstance<RowDataType>): boolean => {
    return !header.subHeaders.length && header.column.getCanSort();
  };

  const theme = getCurrentTheme();
  const getSortingBtnIcon = (column: GridColumnInstance<RowDataType>): IconType => {
    const sortedState = column.getIsSorted();
    const isLightTheme = theme === 'light';
    let icon: IconType = 'icon-sorting-default';
    if (sortedState === 'asc') {
      icon = isLightTheme ? 'icon-sorting-desc' : 'icon-sorting-asc';
    } else if (sortedState === 'desc') {
      icon = isLightTheme ? 'icon-sorting-asc' : 'icon-sorting-desc';
    }
    return icon;
  };

  const headerExpandBtnIcon = computed<IconType>(() =>
    table.value?.getIsAllRowsExpanded()
      ? (expandingInfo.value.expandedIcon as IconType)
      : (expandingInfo.value.collapsedIcon as IconType),
  );

  return {
    getHeaderCellAttributes,
    getHeaderCellStyle,
    getHeaderCellClasses,
    getUseHeaderCellOrdering,
    getCellHeight,
    getIsVisibleOrderingHandle,
    getClickableHeader,
    getSortingBtnIcon,
    headerExpandBtnIcon,
  };
};
